import {GenericObjectTypeInfo} from '@catcore/types/base';
import {camelize} from '@catcore/utils';
import {isNullOrUndefined} from 'util';

/**
 * This module provides the tools to create a valid JSON Schema result based on the defined drafts that can be found here:
 * json-schema.org
 *
 * The module exports a single factory function that expects a valid data schema structure that is provided by the current WebAPI
 * implementation of API Platform.
 *
 * The factory function will return a valid JSON Schema structure which can be used to create form structure, validators or whatever
 */

/**
 * All supported object types of a JSON Schema
 */
type JsonSchemaEntityType = 'object' | 'string' | 'number' | 'boolean' | 'array';

/**
 * The skeleton information of an JSON Schema model, extended with properties which are only required for the C.A.T. project.
 *
 * Custom properties are:
 * - readOnly
 * - writeOnly
 * - writeableOnCreate
 */
export interface JsonSchemaEntity {
  type: JsonSchemaEntityType;
  resourceRef: string;
  resourceUri: string;

  // meta
  readOnly?: boolean;
  writeOnly?: boolean;
  writeableOnCreate?: boolean;

  // numeric
  maximum?: number;
  minimum?: number;

  choices?: string[];

  // strings
  maxLength?: number;
  minLength?: number;
  pattern?: string;

  // arrays
  maxItems?: number;
  minItems?: number;
}

interface ValidatorConstraint {
  constraint_type: string;
  choices: string[];
  min: number;
  max: number;
  message: string;
  multiple_message: string;
  min_message?: string;
  max_message?: string;
}

const ConstraintType = {
  choice: 'choice',
};

/**
 * Determines very rough, if a string is in a pluralized version
 * @param {string} value
 * @returns {boolean}
 */
const isPluralized = (value: string): boolean => {
  return !isNullOrUndefined(value.match(/.*s$/));
};

/**
 * Determine the possible object type of the provided property name.
 * Returns on of JsonSchemaEntityType.
 *
 * @param {string} propertyName
 * @param {string} originalType
 * @param rawInfo
 * @returns {string}
 */
const determineType = (propertyName: string, originalType: string, rawInfo: any) => {
  let returnableType = originalType;

  if (['int', 'float', GenericObjectTypeInfo.Number].includes(returnableType)) {
    returnableType = GenericObjectTypeInfo.Number;
  }
  if (['bool', GenericObjectTypeInfo.Boolean].includes(returnableType)) {
    returnableType = GenericObjectTypeInfo.Boolean;
  }
  if ([GenericObjectTypeInfo.Object].includes(returnableType) && rawInfo.type && rawInfo.type.collection) {
    returnableType = GenericObjectTypeInfo.Array;
  }

  return returnableType;
};

/**
 * Fetch and return Property type specific properties
 * @param propertyType
 * @param propertyInfo
 * @returns {{}}
 */
const propertiesByType = (propertyType, propertyInfo) => {
  const properties = {};

  propertyInfo.validator_constraints.forEach((constraint: ValidatorConstraint) => {
    if (constraint.constraint_type === ConstraintType.choice) {
      properties['choices'] = constraint.choices;
    }
  });

  return properties;
};

const buildGenericResourceUriFromName = (value: string) => {
  return `/api/${value.replace(/\_/g, '-')}`;
};

/**
 * Creating a JSON Schema for a single property
 * @param entityProperties
 * @returns {{values: {}; required: any[]}}
 */
const resolvePropertiesForJSONSchema = (entityProperties) => {
  const propertyNames = Object.keys(entityProperties);
  const propertyMap = {values: {}, required: []};

  propertyNames.forEach((propertyName) => {
    const propertyInfo = entityProperties[propertyName];
    const propertyType = determineType(propertyName, propertyInfo.type.builtin_type, propertyInfo);
    let resourceRef = propertyInfo.type.resource_uri;
    let resourceUri;

    resourceRef = resourceRef || (propertyInfo.type.collection_value_type && propertyInfo.type.collection_value_type.resource_uri);
    resourceRef = resourceRef || propertyName;

    resourceUri = buildGenericResourceUriFromName(resourceRef);

    if (propertyInfo.required) {
      propertyMap.required.push(propertyName);
    }

    propertyMap.values[propertyName] = {
      type: propertyType,
      readOnly: propertyInfo.readable && !propertyInfo.writable,
      writeableOnCreate: propertyInfo.constructor_parameter || propertyInfo.writable,
      resourceUri,
      resourceRef,
      ...propertiesByType(propertyType, propertyInfo),
    } as JsonSchemaEntity;
  });

  return propertyMap;
};

export const EntitySchemaType = {};

export const toJSONSchema = (rawEntitySchema: any): any => {
  const jsonSchema = {$schema: null, type: GenericObjectTypeInfo.Object, properties: {}};
  const entityNames = Object.keys(rawEntitySchema);

  entityNames.forEach((entityName) => {
    const useableEntityName = entityName;
    const properties = rawEntitySchema[entityName].properties;
    const propertyInfos = resolvePropertiesForJSONSchema(properties);
    jsonSchema.properties[useableEntityName] = {
      properties: propertyInfos.values,
      required: propertyInfos.required,
      resourceUri: buildGenericResourceUriFromName(useableEntityName)
    };
    EntitySchemaType[camelize(useableEntityName)] = useableEntityName;
  });
  console.log('jsonSchema: ', jsonSchema, EntitySchemaType);
  return jsonSchema;
};



// WEBPACK FOOTER //
// /agent/workspace/CUS-LHT_NICE_nto_nto_CAT_develop@2/app/Resources/Public/src/app/modules/core/utils/json-schema/entity-schema-to-json-schema.ts